<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end form-offcanvas"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="offcanvas-header border-bottom bg-white py-3">
      <h6 class="offcanvas-title fw-bold mb-0 d-flex align-items-center gap-2">
        <i class="fa-solid fa-book-open text-theme" />
        {{ type === 'CREATE'? $t('Create Recipe'): $t('Edit Recipe')  }}
      </h6>
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body bg-body-secondary p-3"
      v-else
    >
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-circle-info" />
          <span>{{ $t('Recipe Information') }}</span>
        </div>
        <div class="px-3 pt-3">
          <div class="row g-3 py-2">
            <div class="col-md-8">
              <label
                for="name"
                class="form-label"
              >
                {{ $t('Recipe Name') }}
                <span class="text-danger">*</span>
              </label>
              <div class="input-group-sm">
                <input
                  id="name"
                  type="text"
                  v-model.trim="v$.form.name.$model"
                  class="form-control shadow-none"
                  :placeholder="$t('Enter recipe name') + '...'"
                >
                <div v-for="error of v$.form.name.$errors"
                  class="d-block invalid-feedback"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label" for="flexSwitchCheckStatus">
                {{ $t('Status') }}
              </label>
              <div class="form-check form-switch">
                <input
                  class="form-check-input shadow-none"
                  type="checkbox" id="flexSwitchCheckStatus"
                  v-model.trim="v$.form.is_active.$model"
                >
                <label class="form-check-label" for="flexSwitchCheckStatus">
                  {{ $t('Active') }}
                </label>
              </div>
            </div>
            <div class="col-md-12">
              <label for="description" class="form-label">
                {{ $t('Description') }}
              </label>
              <vue-editor
                v-model="v$.form.description.$model"
                :editor-toolbar="editor.toolbar"
                :placeholder="$t('Optional description') + '...'"
              />
            </div>
            <div
              class="col-md-12"
              v-if="hasPermissionConfidential"
            >
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input shadow-none"
                  v-model.trim="v$.form.is_confidential.$model"
                  type="checkbox"
                  id="checkRequired"
                  value="option1"
                >
                <label
                  class="form-check-label"
                  for="checkRequired"
                >
                  <b class="text-danger">
                    <i class="fa-solid fa-lock me-1"/>
                    {{ $t('Confidential Recipe') }}
                  </b>
                  <br>
                  <span
                    class="text-secondary"
                    style="font-size: 14px;"
                  >
                    {{ $t('Marking this recipe as confidential will hide its details and ingredients. Only users with authorized high-level permissions will be able to view them.') }}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-arrow-right-to-bracket" />
          <span>{{ $t('Inputs (Ingredients to consume)') }}</span>
        </div>
        <div class="px-3 pt-3">
          <div class="row g-3 py-2">
            <div class="col-md-12 mt-0" v-if="form.inputs.length > 0">
              {{ $t('Inventory') }}
            </div>
            <div class="col-md-12 mt-1" v-if="form.inputs.length > 0">
              <div class="border rounded">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item" v-for="(item, index) in form.inputs" :key="index">
                    <input-item-form
                      :index="index"
                      :item="item"
                    />
                  </li>
                </ul>
              </div>
            </div>
            <div
              class="col-md-4 mt-1"
            >
              <select2
                v-model="selectInventory"
                @select="onSelectedInventoryInput"
                :options="select2ItemInputs.options"
                :settings="select2ItemInputs.setting"
                :placeholder="$t('Add Input Line') + '...'"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-arrow-right-from-bracket" />
          <span>{{ $t('Outputs (Results produced)') }}</span>
        </div>
        <div class="px-3 pt-3">
          <div class="row g-3 py-2">
            <div class="col-md-12 mt-0" v-if="form.outputs.length > 0">
              {{ $t('Product / Inventory') }}
            </div>
            <div class="col-md-12 mt-1" v-if="form.outputs.length > 0">
              <div class="border rounded">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item" v-for="(item, index) in form.outputs" :key="index">
                    <output-item-form
                      :index="index"
                      :item="item"
                      :options-shelf-life-units="optionsShelfLifeUnits"
                    />
                  </li>
                </ul>
              </div>
            </div>
            <div
              class="col-md-4 mt-1"
            >
              <select2
                v-model="selectInventory"
                @select="onSelectedInventoryOutput"
                :options="select2ItemOutputs.options"
                :settings="select2ItemOutputs.setting"
                :placeholder="$t('Add Output Line') + '...'"
              />
            </div>
            <div
              class="col-md-8 mt-0 text-end text-success align-content-end"
              v-if="form.outputs.length > 0"
              style="font-size: 14px;"
              :class="[form.cost_allocation != 100? 'text-danger': 'text-success']"
            >
              <b>
                {{ $t('Total Cost Allocation') }}
              </b>
              {{ ': ' + formatNumberTwoDigit(total) + '% ' }}
              <b>
                {{ '(' + (form.cost_allocation != 100? $t('Invalid'): $t('Valid')) + ')' }}
              </b>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-theme btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-theme btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { InventoryRecipes } from '@/services/endpoint/inventory-recipes.service';
import { AUTH, API_URLS } from '@/services/endpoint/_constants';
import Select2 from 'vue3-select2-component';
import moment from "moment";
import signature  from '@/services/signature';
import InputItemForm from './InputItemForm.vue';
import OutputItemForm from './OutputItemForm.vue';
import { VueEditor } from "vue3-editor";
import numeral from 'numeral';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    let vm = this;
    return {
      selectInventory: [],
      hasPermission: false,
      listItems: [],
      loading: true,
      loadingSave: false,
      editor: {
        toolbar: [
          [{ header: [false, 1, 2, 3, 4, 5, 6] }],
          ["bold", "italic", "underline", "strike"],
          [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ color: [] }],
        ],
      },
      select2ItemInputs: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
          multiple: true,
          language: {
            maximumSelected: function (e) {
              return vm.$t('You can only select') + ' ' + e.maximum + ' ' + vm.$t('items');
            },
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.API_RAW_MATERIAL_RECIPE_ITEM_INPUT,
            data: function (params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                let disabled = false;
                disabled = vm.form.inputs.find((inventory) => inventory.inventory_id == item.id)? true: false;
                if (!disabled) disabled = vm.form.outputs.find((inventory) => inventory.inventory_id == item.id)? true: false;
                return {
                  id: item.id,
                  text: item.name,
                  unit_id: item.unit_id,
                  unit_name: item.unit_name,
                  disabled: disabled
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          }
        }
      },
      select2ItemOutputs: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
          multiple: true,
          language: {
            maximumSelected: function (e) {
              return vm.$t('You can only select') + ' ' + e.maximum + ' ' + vm.$t('items');
            },
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.API_RAW_MATERIAL_RECIPE_ITEM_OUTPUT,
            data: function (params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                let disabled = false;
                disabled = vm.form.inputs.find((inventory) => inventory.inventory_id == item.id)? true: false;
                if (!disabled) disabled = vm.form.outputs.find((inventory) => inventory.inventory_id == item.id)? true: false;
                return {
                  id: item.id,
                  text: item.name,
                  unit_id: item.unit_id,
                  unit_name: item.unit_name,
                  disabled: disabled
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          }
        }
      },
      form: {
        name: '',
        inputs: [],
        outputs: [],
        description: '',
        is_active: true,
        is_confidential: false,
        cost_allocation: 100
      },
      optionsShelfLifeUnits: [],
      optionsExpiryAnchors: []
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    Select2,
    InputItemForm,
    OutputItemForm,
    AccessDenied,
    VueEditor
  },
  methods: {
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    async deleteItemInput(index) {
      this.form.inputs.splice(index, 1);
      this.select2ItemInputs.options = [];
    },
    async deleteItemOutput(index) {
      this.form.outputs.splice(index, 1);
      this.select2ItemOutputs.options = [];
    },
    async onSelectedInventoryInput(e) {
      let vm = this;
      this.selectInventory = [];
      if (!e || !e.id) return;
      if (this.form.inputs.find((item) => item.inventory_id == e.id)) return;
      if (this.form.outputs.find((item) => item.inventory_id == e.id)) return;
      this.form.inputs.push({
        inventory_id: e.id,
        name: e.text,
        unit_id: e.unit_id,
        unit_name: e.unit_name,
        quantity: '',
      });
      this.select2ItemInputs.options = [];
    },
    async onSelectedInventoryOutput(e) {
      let vm = this;
      this.selectInventory = [];
      if (!e || !e.id) return;
      if (this.form.inputs.find((item) => item.inventory_id == e.id)) return;
      if (this.form.outputs.find((item) => item.inventory_id == e.id)) return;
      this.form.outputs.push({
        inventory_id: e.id,
        name: e.text,
        unit_id: e.unit_id,
        unit_name: e.unit_name,
        quantity: '',
        shelf_life_value: '',
        shelf_life_unit: 'day',
        cost_allocation_percent: ''
      });
      this.select2ItemOutputs.options = [];
    },
    async getCreateData() {
      await InventoryRecipes.getCreate().then(res => {
        this.optionsShelfLifeUnits = res.data.data.outputs.shelf_life_unit;
        this.hasPermissionConfidential = res.data.data.has_permission_confidential;
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    async getUpdateData() {
      await InventoryRecipes.getUpdate(this.id).then(res => {
        this.optionsShelfLifeUnits = res.data.filter.shelf_life_unit;
        this.hasPermissionConfidential = res.data.filter.has_permission_confidential;
        this.form = {
          name: res.data.data.name,
          inputs: res.data.data.inputs.map(function(input) {
            return {
              id: input.id,
              inventory_id: input.inventory_id,
              name: input.name,
              unit_id: input.unit_id,
              unit_name: input.unit_name,
              quantity: input.quantity
            }
          }),
          outputs: res.data.data.outputs.map(function(output) {
            return {
              id: output.id,
              inventory_id: output.inventory_id,
              name: output.name,
              unit_id: output.unit_id,
              unit_name: output.unit_name,
              quantity: output.quantity,
              shelf_life_value: output.shelf_life_value,
              shelf_life_unit: output.shelf_life_unit,
              cost_allocation_percent: output.cost_allocation_percent
            }
          }),
          description: res.data.data.description,
          is_active: res.data.data.active,
          is_confidential: res.data.data.confidential
        };
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    setParamsBeforeSendCreate() {
      let params = _.clone(this.form);
      delete params.cost_allocation;
      return $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    setParamsBeforeSendUpdate() {
      let params = _.clone(this.form);
      delete params.cost_allocation;
      return $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await InventoryRecipes.update(this.id, this.setParamsBeforeSendUpdate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Recipe!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.errors) {
          this.errors = err?.response?.data?.errors;
        }
        this.loadingSave = false;
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await InventoryRecipes.create(this.setParamsBeforeSendCreate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Recipe!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.errors) {
          this.errors = err?.response?.data?.errors;
        }
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  },
  computed: {
    total() {
      this.form.cost_allocation = this.form.outputs.reduce((sum, value) => {
        return sum + (value.cost_allocation_percent*1);
      }, 0);

      return this.form.cost_allocation;
    }
  }
}
</script>
